import type { NativeWidgetIntent } from '../../services/nativeWidgetBridge'
import type { HelperAssignedItem } from '../../services/helper/helperAssignedItemsService'
import type { HelperTabKey } from '../../services/helper/helperStore'
import type { HelperFieldSubtab, HelperControlsSubtab } from './helperFieldModel'
import { openWorkspaceTarget } from '../../services/workspaceSwitch/openWorkspaceTarget'

export type HelperRouteView = HelperTabKey | 'PROFILE'

export type HelperNativeWidgetIntentResolution =
  | { kind: 'OPEN_TASK'; tab: 'TASKS'; itemId: string; mode: 'DETAILS' | 'SCANNER' }
  | { kind: 'OPEN_TAB'; tab: HelperTabKey; fieldSubtab?: HelperFieldSubtab; controlsSubtab?: HelperControlsSubtab }

export const helperViewFromPathname = (pathname: string): HelperRouteView => {
  if (pathname.includes('?')) {
    const search = pathname.slice(pathname.indexOf('?'))
    if (new URLSearchParams(search).get('request')) return 'OPS'
  }
  const segment = pathname
    .split(/[?#]/, 1)[0]
    .split('/')
    .filter(Boolean)[1]
    ?.toLowerCase()
  if (segment === 'chat' || segment === 'nexus') return 'CHAT'
  if (segment === 'meetings') return 'MEETINGS'
  if (segment === 'calendar') return 'CALENDAR'
  if (segment === 'profile') return 'PROFILE'
  if (segment === 'field' || segment === 'scan' || segment === 'controls') return 'FIELD'
  if (segment === 'school') return 'SCHOOL'
  if (segment === 'ops') return 'OPS'
  return 'TASKS'
}

export const resolveHelperNativeWidgetIntent = (
  intent: NativeWidgetIntent,
  items: HelperAssignedItem[],
  activeWorkspaceId: string | null,
  options?: {
    openWorkspaceTarget?: typeof openWorkspaceTarget
  },
): HelperNativeWidgetIntentResolution => {
  if (intent.workspaceId && activeWorkspaceId && intent.workspaceId !== activeWorkspaceId) {
    const fn = options?.openWorkspaceTarget ?? openWorkspaceTarget
    void fn({
      workspaceId: intent.workspaceId,
      source: 'WIDGET',
    })
  }

  const currentWidgetItem = (targetId?: string | null) => {
    if (targetId) return items.find((entry) => entry.id === targetId) ?? null
    return items.find((entry) => entry.workspaceId === activeWorkspaceId) ?? items[0] ?? null
  }

  if (intent.action === 'helper_tasks') {
    const item = intent.targetId ? currentWidgetItem(intent.targetId) : null
    return item
      ? { kind: 'OPEN_TASK', tab: 'TASKS', itemId: item.id, mode: 'DETAILS' }
      : { kind: 'OPEN_TAB', tab: 'TASKS' }
  }

  if (intent.action === 'helper_scan') {
    const item = currentWidgetItem(intent.targetId) ?? currentWidgetItem()
    return item
      ? { kind: 'OPEN_TASK', tab: 'TASKS', itemId: item.id, mode: 'SCANNER' }
      : { kind: 'OPEN_TAB', tab: 'FIELD', fieldSubtab: 'SCAN', controlsSubtab: 'SCAN' }
  }

  if (intent.action === 'helper_proof' || intent.action === 'helper_objective') {
    const item = currentWidgetItem(intent.targetId)
    return item
      ? { kind: 'OPEN_TASK', tab: 'TASKS', itemId: item.id, mode: 'DETAILS' }
      : { kind: 'OPEN_TAB', tab: 'TASKS' }
  }

  return { kind: 'OPEN_TAB', tab: 'TASKS' }
}
